import * as AlertDialog from '@radix-ui/react-alert-dialog';
import type { ResourceDetail } from '@uit/shared/schemas/resource';
import { ResourceDetail as ResourceDetailSchema } from '@uit/shared/schemas/resource';
import { useApi } from '../../lib/api.js';
import s from './ReassignVendorAlert.module.scss';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  engagementId: string;
  resourceId: string;
  resourceName: string;
  oldVendorName: string;
  newVendorId: string;
  newVendorName: string;
  referredByVendorName: string | null;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
}

/**
 * The consequences confirm for a vendor reassignment (T-172). This modal is the
 * whole reason the reassignment is not a plain field edit: the vendor lives on
 * the RESOURCE, so changing it here moves every engagement that resource has.
 * The admin has to see that ripple — by name — before it happens.
 *
 * The sibling list comes from the resource detail the page already has an
 * endpoint for; there is no bespoke preview API.
 */
export function ReassignVendorAlert({
  open,
  onOpenChange,
  engagementId,
  resourceId,
  resourceName,
  oldVendorName,
  newVendorId,
  newVendorName,
  referredByVendorName,
  busy,
  error,
  onConfirm,
}: Props) {
  const { data: resource } = useApi<ResourceDetail>(
    open ? `/api/resources/${resourceId}` : null,
    ResourceDetailSchema,
  );

  const siblings = (resource?.engagements ?? []).filter((e) => e.id !== engagementId);
  const activeSiblings = siblings.filter((e) => e.status === 'active');

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={s.overlay} />
        <AlertDialog.Content className={s.content}>
          <AlertDialog.Title className={s.title}>
            Move this engagement to {newVendorName}?
          </AlertDialog.Title>
          <AlertDialog.Description className={s.description}>
            The vendor lives on the <strong>resource</strong>, not the engagement — this changes who
            uIT pays for <strong>everything {resourceName} works on</strong>, not just this row.
          </AlertDialog.Description>

          <div className={s.effects}>
            <Effect icon="👤" title="The resource moves vendors">
              <span className={s.move}>
                {resourceName} · {oldVendorName} → {newVendorName}
              </span>
            </Effect>

            <Effect
              icon="🔗"
              title={
                siblings.length === 1
                  ? '1 sibling engagement follows'
                  : `${siblings.length} sibling engagements follow`
              }
            >
              {siblings.length === 0 ? (
                <>This resource has no other engagements.</>
              ) : (
                <>
                  All of this resource&rsquo;s engagements show the new vendor:
                  <ul className={s.list}>
                    {siblings.map((e) => (
                      <li key={e.id}>
                        {e.product_service} — {e.status}
                        {e.end_date ? ` (ended ${e.end_date})` : ''}
                      </li>
                    ))}
                  </ul>
                  {activeSiblings.length > 0 && (
                    <div className={s.subNote}>
                      Each engagement carries its own signed contract — active ones (
                      {activeSiblings.map((e) => e.product_service).join(', ')}) may need the new
                      agreement attached on their own pages too.
                    </div>
                  )}
                </>
              )}
            </Effect>

            <Effect icon="🧾" title="Billing history is untouched">
              Past runs stay under {oldVendorName}&rsquo;s bill numbers — history is never
              rewritten.
            </Effect>

            <Effect icon="📗" title="This engagement row survives">
              Same QuickBooks Service item — no duplicate is created. The next billing run bills it
              under the new QB Vendor.
            </Effect>

            {referredByVendorName && (
              <Effect icon="🤝" title="Referral link carries over">
                Referred by {referredByVendorName} — unchanged.
              </Effect>
            )}

            <Effect icon="📄" title="New contract needed">
              The agreement on file is with {oldVendorName}. Attach the new one in this page&rsquo;s
              Contract section — the engagement repoints and the old document is kept.
            </Effect>
          </div>

          {error && (
            <div className={s.error} role="alert">
              {error}
            </div>
          )}

          <div className={s.foot}>
            <span className={s.audit}>
              Writes an <code>audit_log</code> entry — admin, timestamp, old → new.
            </span>
            <span className={s.buttons}>
              <AlertDialog.Cancel asChild>
                <button type="button" className={s.cancel} disabled={busy}>
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <button
                type="button"
                className={s.confirm}
                disabled={busy}
                data-vendor-id={newVendorId}
                onClick={onConfirm}
              >
                {busy ? 'Reassigning…' : 'Reassign vendor'}
              </button>
            </span>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

function Effect({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className={s.effect}>
      <span className={s.effectIcon} aria-hidden="true">
        {icon}
      </span>
      <div>
        <div className={s.effectTitle}>{title}</div>
        <div className={s.effectBody}>{children}</div>
      </div>
    </div>
  );
}
